import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { ACCENT, LIGHT, DARK, FONTS } from './tokens';

/**
 * Qiyas Design Identity — MUI theme (classic chic, teal signature).
 * All colors derive from theme/tokens.ts — do not add raw hex here that has a
 * token. Density metrics (28px controls, radius 0) are part of the identity.
 */

const headingSerif = { fontFamily: FONTS.serif, fontWeight: 600 } as const;

const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: ACCENT.deep, // light-mode interactive (AA on ledger surfaces)
      light: ACCENT.core,
      dark: ACCENT.deeper,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: LIGHT.ink, // ink chrome
      light: LIGHT.inkSoft,
      dark: '#10151C',
      contrastText: '#FFFFFF',
    },
    background: {
      default: LIGHT.ground,
      paper: LIGHT.surface,
    },
    text: {
      primary: LIGHT.ink,
      secondary: LIGHT.inkSoft,
    },
    divider: LIGHT.rule,
    success: { main: LIGHT.up },
    error: { main: LIGHT.down },
    warning: { main: LIGHT.warn },
    info: { main: LIGHT.info },
    action: {
      hover: 'rgba(27, 34, 48, 0.05)',
      selected: ACCENT.tint,
    },
  },
  typography: {
    fontFamily: FONTS.sans,
    h1: { ...headingSerif, fontSize: '2.5rem', letterSpacing: '0.01em' },
    h2: { ...headingSerif, fontSize: '2rem', letterSpacing: '0.01em' },
    h3: { ...headingSerif, fontSize: '1.75rem', letterSpacing: '0.01em' },
    h4: { ...headingSerif, fontSize: '1.5rem', letterSpacing: '0.01em' },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    overline: { letterSpacing: '0.18em', fontWeight: 600 },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.04em',
          borderRadius: 0,
          fontSize: '0.7rem',
          padding: '2px 12px',
          minHeight: 28,
          lineHeight: 1.2,
        },
        sizeSmall: {
          fontSize: '0.65rem',
          padding: '1px 8px',
          minHeight: 24,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: `1px solid ${LIGHT.rule}`,
          borderRadius: 0,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        size: 'small' as any,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          height: 28,
          fontSize: '0.72rem',
          backgroundColor: LIGHT.surface2,
          '& fieldset': { borderColor: LIGHT.inputBorder },
          '&:hover fieldset': { borderColor: ACCENT.deep },
          '&.Mui-focused fieldset': { borderColor: ACCENT.deep },
        },
        input: {
          padding: '4px 8px',
          height: 'auto',
        },
        inputSizeSmall: {
          padding: '4px 8px',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: 'small' as any,
      },
      styleOverrides: {
        root: {
          fontSize: '0.72rem',
        },
        sizeSmall: {
          transform: 'translate(8px, 5px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 600,
          letterSpacing: '0.04em',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '0 !important',
          '&:first-of-type': { borderRadius: '0 !important' },
          '&:last-of-type': { borderRadius: '0 !important' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 32,
          '&.Mui-expanded': { minHeight: 32 },
        },
        content: {
          margin: '4px 0',
          '&.Mui-expanded': { margin: '4px 0' },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: '0.65rem',
          padding: '2px 8px',
          minHeight: 26,
          lineHeight: 1.2,
          textTransform: 'uppercase' as const,
          fontWeight: 600,
          borderRadius: 0,
          '&.Mui-selected': {
            backgroundColor: ACCENT.tint,
            color: ACCENT.deep,
          },
        },
        sizeSmall: {
          fontSize: '0.6rem',
          padding: '1px 6px',
          minHeight: 22,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: LIGHT.header,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
            color: LIGHT.inkSoft,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': { backgroundColor: ACCENT.deep, height: 2 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${LIGHT.ruleStrong}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
};

const darkThemeOptions: ThemeOptions = {
  ...lightThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: DARK.accent, // the anchor #5FA892 is the dark-mode interactive
      light: '#8AC7B4',
      dark: ACCENT.deep,
      contrastText: '#10151C',
    },
    secondary: {
      main: DARK.ink,
      light: '#F7F5EF',
      dark: DARK.inkSoft,
      contrastText: DARK.ground,
    },
    background: {
      default: DARK.ground,
      paper: DARK.surface,
    },
    text: {
      primary: DARK.ink,
      secondary: DARK.inkSoft,
    },
    divider: DARK.rule,
    success: { main: DARK.up },
    error: { main: DARK.down },
    warning: { main: DARK.warn },
    info: { main: DARK.info },
    action: {
      active: DARK.ink,
      hover: 'rgba(236, 232, 222, 0.06)',
      selected: ACCENT.tint,
      disabled: 'rgba(236, 232, 222, 0.3)',
      disabledBackground: 'rgba(236, 232, 222, 0.12)',
    },
  },
  components: {
    ...lightThemeOptions.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: DARK.ground,
          scrollbarColor: `${DARK.ruleStrong} ${DARK.ground}`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: DARK.ground,
          },
          '&::-webkit-scrollbar-thumb': {
            background: DARK.ruleStrong,
            borderRadius: '4px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: DARK.muted,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { color: DARK.inkSoft },
        label: { color: DARK.inkSoft },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: DARK.muted,
          '&.Mui-checked': { color: DARK.accent },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: DARK.muted,
          '&.Mui-checked': { color: DARK.accent },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: DARK.muted,
          '&.Mui-focused': { color: DARK.accent },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: DARK.muted },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: { color: 'inherit' },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(236, 232, 222, 0.06)' },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { color: DARK.ink },
        secondary: { color: DARK.muted },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface,
          '&:before': { backgroundColor: DARK.rule },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { backgroundColor: DARK.surface2 },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: { backgroundColor: DARK.surface },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.ground,
          backgroundImage: 'none',
          borderBottom: `1px solid ${DARK.rule}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#10151C',
          borderRight: `1px solid ${DARK.rule}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface,
          backgroundImage: 'none',
          border: `1px solid ${DARK.rule}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { backgroundColor: DARK.surface },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.input,
          '& .MuiTableCell-head': {
            backgroundColor: DARK.input,
            color: DARK.ink,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
            borderBottom: `1px solid ${DARK.ruleStrong}`,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface,
          '& .MuiTableRow-root': {
            backgroundColor: DARK.surface,
            '&:nth-of-type(odd)': { backgroundColor: DARK.surface2 },
            '&:nth-of-type(even)': { backgroundColor: DARK.surface },
            '&:hover': { backgroundColor: `${ACCENT.tint} !important` },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface,
          '&.MuiTableRow-hover:hover': {
            backgroundColor: `${ACCENT.tint} !important`,
          },
        },
        head: { backgroundColor: `${DARK.input} !important` },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${DARK.rule}`,
          color: DARK.inkSoft,
          backgroundColor: 'inherit',
        },
        head: {
          backgroundColor: `${DARK.input} !important`,
          color: `${DARK.ink} !important`,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: DARK.input,
            '& fieldset': { borderColor: DARK.ruleStrong },
            '&:hover fieldset': { borderColor: DARK.accent },
            '&.Mui-focused fieldset': { borderColor: DARK.accent },
          },
          '& .MuiInputLabel-root': { color: DARK.muted },
          '& .MuiInputBase-input': { color: DARK.ink },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          height: 28,
          fontSize: '0.72rem',
          backgroundColor: DARK.input,
          '& fieldset': { borderColor: DARK.ruleStrong },
          '&:hover fieldset': { borderColor: DARK.accent },
          '&.Mui-focused fieldset': { borderColor: DARK.accent },
        },
        input: { color: DARK.ink, padding: '4px 8px', height: 'auto' },
        inputSizeSmall: { padding: '4px 8px' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { color: DARK.ink },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { backgroundColor: DARK.input },
        icon: { color: DARK.muted },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: DARK.surface,
          border: `1px solid ${DARK.rule}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(236, 232, 222, 0.06)' },
          '&.Mui-selected': {
            backgroundColor: ACCENT.tint,
            '&:hover': { backgroundColor: ACCENT.tintStrong },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: DARK.surface,
          backgroundImage: 'none',
          border: `1px solid ${DARK.ruleStrong}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { backgroundColor: DARK.input },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { backgroundColor: ACCENT.tint },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': { backgroundColor: DARK.accent, height: 2 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 600,
          letterSpacing: '0.04em',
        },
        filled: { backgroundColor: DARK.surface2 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.04em',
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: DARK.accent,
            color: '#10151C',
            '&:hover': { backgroundColor: '#8AC7B4' },
          },
        },
        outlined: {
          borderColor: DARK.ruleStrong,
          color: DARK.ink,
          '&:hover': {
            borderColor: DARK.accent,
            backgroundColor: ACCENT.tint,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: DARK.muted,
          '&:hover': { backgroundColor: 'rgba(236, 232, 222, 0.06)' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: DARK.rule },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: DARK.surface2,
          color: DARK.ink,
          border: `1px solid ${DARK.rule}`,
          borderRadius: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: DARK.surface },
        listbox: { backgroundColor: DARK.surface },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { backgroundColor: DARK.surface, borderRadius: 0 },
        standardInfo: {
          backgroundColor: 'rgba(126, 157, 190, 0.15)',
          color: '#7E9DBE',
        },
        standardSuccess: {
          backgroundColor: 'rgba(127, 191, 122, 0.15)',
          color: '#7FBF7A',
        },
        standardWarning: {
          backgroundColor: 'rgba(203, 168, 103, 0.15)',
          color: '#E0C89A',
        },
        standardError: {
          backgroundColor: 'rgba(217, 141, 141, 0.15)',
          color: '#E7CECE',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: DARK.ruleStrong },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: DARK.surface2 },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(236, 232, 222, 0.11)' },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: DARK.surface2,
          color: DARK.ink,
        },
      },
    },
  },
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);

/* ─── Workstation chrome tokens ─────────────────────────────────────────────
 * Dense institutional-desktop tokens consumed by
 * components/ui/WorkstationLayout.tsx (the `WS` object + getWS()). Derived
 * from theme/tokens.ts so the workstation chrome carries the same classic-chic
 * identity as the rest of the portal (ledger stock + ink + teal signature),
 * in both light and dark.
 */
export interface WorkstationTokens {
  BORDER: string;
  BG_PANEL: string;
  BG_INPUT: string;
  BG_READONLY: string;
  BG_HEADER: string;
  BG_TOOLBAR: string;
  BG_GRID: string;
  BG_YELLOW: string;
  TEXT_LABEL: string;
  TEXT_VALUE: string;
  TEXT_READONLY: string;
  TEXT_LEGEND: string;
  INPUT_BORDER: string;
  ACCENT: string;
  BTN_TEXT: string;
  BTN_HOVER: string;
  BTN_ACTIVE: string;
  FONT: string;
}

export const WS_FONT = FONTS.sans;

export const getWorkstationTokens = (theme: Theme): WorkstationTokens => {
  const p = theme.palette;
  if (p.mode === 'dark') {
    return {
      BORDER: `1px solid ${p.divider}`,
      BG_PANEL: p.background.default,
      BG_INPUT: DARK.input,
      BG_READONLY: DARK.surface2,
      BG_HEADER: DARK.input,
      BG_TOOLBAR: p.background.paper,
      BG_GRID: p.background.paper,
      BG_YELLOW: 'rgba(203, 168, 103, 0.12)',
      TEXT_LABEL: p.text.secondary,
      TEXT_VALUE: p.text.primary,
      TEXT_READONLY: p.text.secondary,
      TEXT_LEGEND: p.text.secondary,
      INPUT_BORDER: DARK.ruleStrong,
      ACCENT: DARK.accent,
      BTN_TEXT: p.text.primary,
      BTN_HOVER: ACCENT.tint,
      BTN_ACTIVE: ACCENT.tintStrong,
      FONT: WS_FONT,
    };
  }
  return {
    BORDER: `1px solid ${LIGHT.ruleStrong}`,
    BG_PANEL: LIGHT.panel,
    BG_INPUT: LIGHT.surface2,
    BG_READONLY: LIGHT.ground,
    BG_HEADER: LIGHT.header,
    BG_TOOLBAR: LIGHT.toolbar,
    BG_GRID: LIGHT.surface2,
    BG_YELLOW: LIGHT.highlight,
    TEXT_LABEL: LIGHT.inkSoft,
    TEXT_VALUE: LIGHT.ink,
    TEXT_READONLY: LIGHT.muted,
    TEXT_LEGEND: '#57503F',
    INPUT_BORDER: LIGHT.inputBorder,
    ACCENT: ACCENT.deep,
    BTN_TEXT: LIGHT.ink,
    BTN_HOVER: ACCENT.tint,
    BTN_ACTIVE: ACCENT.tintStrong,
    FONT: WS_FONT,
  };
};
